import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AxiosResponse, isAxiosError } from 'axios';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import { isNetworkError } from 'src/utils/isNetworkError';

import useGetGalenErrorMsg from './useGetGalenErrorMsg';

type ClassifiedError = {
  message: string;
  isNetworkError: boolean;
};

const useHandleHttpRequestError = () => {
  const { getErrorMessage } = useGetGalenErrorMsg();

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const { setIsConnected } = useRemoteBoundStore();

  const extractErrorMessage = useCallback((data: string): string => {
    const errorMessageParts = data.split(' ');
    return errorMessageParts.length > 1 ? errorMessageParts[1] : data;
  }, []);

  const handleErrorResponse = useCallback(
    (response: AxiosResponse): string => {
      const { data } = response;
      return getErrorMessage(extractErrorMessage(data));
    },
    [extractErrorMessage, getErrorMessage],
  );

  const classifyError = useCallback(
    (err: unknown): ClassifiedError => {
      if (!isAxiosError(err)) {
        if (err instanceof Error) {
          return {
            message: `${t('bootbox.title.Error')}: ${err.message}`,
            isNetworkError: false,
          };
        }
        return { message: `${err}`, isNetworkError: false };
      }

      const { response, message } = err;

      if (response) {
        return {
          message: handleErrorResponse(response),
          isNetworkError: false,
        };
      }

      if (isNetworkError(err)) {
        return {
          message: t('message.error.networkError'),
          isNetworkError: true,
        };
      }

      // Something happened in setting up the request that triggered an Error
      return {
        message: `${t('bootbox.title.Error')}: ${message}`,
        isNetworkError: false,
      };
    },
    [handleErrorResponse, t],
  );

  const handleHttpRequestError = useCallback(
    (err: unknown): string => classifyError(err).message,
    [classifyError],
  );

  const showHttpRequestError = useCallback(
    (err: unknown, options?: { messagePrefix?: string }): string => {
      const { message, isNetworkError } = classifyError(err);

      if (isNetworkError) {
        setIsConnected(false);
        return message;
      }

      const displayMessage = options?.messagePrefix
        ? `${options.messagePrefix} ${message}`
        : message;

      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: displayMessage,
        locale: language,
        type: 'error',
      });

      return message;
    },
    [classifyError, t, language, setIsConnected],
  );

  return {
    handleHttpRequestError,
    showHttpRequestError,
  };
};

export default useHandleHttpRequestError;
